import moment from 'moment';
import React, { useMemo } from 'react';
import { Controller, FieldError, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { SingleDatePicker } from '../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { HtmlTable } from '../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { InputNumberMask } from '../../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Label } from '../../../../../../CommonComponents/OPL/Label/Label';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { GranTypeDTO } from '../../../../../../Profile/Accounts/api/useAccountsQuery';
import { useStockOptionsContext } from '../../../../context/useStockOptionsContext';
import { FormValues, getDispositionExerciseOptions } from '../../UpsertGrantModal.meta';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';

const emptyVestingSchedule = {
  DispositionStrategy: null,
  DispositionDate: null,
  DispositionYear: null,
  ExerciseStrategy: null,
  ExerciseDate: null,
  ExerciseYear: null,
  VestingDate: null,
  ExpirationDate: null,
  VestedShares: null,
  PricePerShare: null,
};

export const VestingSchedule = () => {
  const { options } = useStockOptionsContext();

  const formBag = useFormContext<FormValues>();

  const {
    control,
    watch,
    formState: { errors },
  } = formBag;

  const fieldArrayBag = useFieldArray<FormValues, 'vestingList', 'key'>({
    control,
    name: 'vestingList',
    keyName: 'key',
  });
  const watchFieldArray = useWatch({ name: 'vestingList', control });
  const vestingList = fieldArrayBag.fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index],
    };
  });

  const [grantType, companyName, pricePerShare] = watch(['grantType', 'name', 'price']);

  const [dispositionOptions, exerciseOptions] = useMemo(
    () => getDispositionExerciseOptions(grantType, options.OptionsPerGrantType),
    [grantType],
  );

  const totalShares = watch('totalShares');

  const totalVestedShares = vestingList.reduce((acc, vest) => acc + (vest.VestedShares ?? 0), 0);
  const sharesToAllocate = (totalShares ?? 0) - totalVestedShares;

  const errorMessages = errors?.vestingList
    ?.map(item => item && Object.values(item).map(err => err.message))
    .flat()
    .filter(Boolean);
  const filteredErrorMessages = errorMessages ? Array.from(new Set(errorMessages)) : [];

  return (
    <Box pt="12">
      {Object.keys(errors).map(
        errorKey =>
          errorKey !== 'vestingList' && (
            <Alert
              variant="error"
              message={(errors[errorKey] as FieldError).message}
              mb="16"
              mx="24"
            />
          ),
      )}
      {filteredErrorMessages.map(errorMsg => (
        <Alert variant="error" message={errorMsg} mb="16" mx="24" />
      ))}

      <Box display="flex" alignItems="center" mx="24" mb="16" css="gap: 12px">
        <Input label="Company Name" value={companyName} disabled css="flex-basis: 48%;" />
        <Input
          label="Price Per Share"
          rightIcon="far fa-dollar-sign"
          value={pricePerShare}
          disabled
          css="flex-basis: 48%;"
        />
      </Box>

      <Text color="neutral.500" px="24" pb="16">
        This vesting schedule assumes{' '}
        <Text as="span" fontWeight={600} color="neutral.500">
          {sharesToAllocate < 0 ? 0 : sharesToAllocate}
        </Text>{' '}
        shares{' '}
        {grantType === GranTypeDTO.Rsu
          ? 'have already vested'
          : 'of this grant have already been exercised'}
        .
      </Text>

      <HtmlTable variant="embedded" edgePadding={24}>
        <table>
          <tbody>
            {vestingList.map((vest, index) => {
              const selectedDisposition = dispositionOptions
                ? dispositionOptions.find(opt => opt.Value === vest.DispositionStrategy)
                : null;
              const selectedExercise = exerciseOptions
                ? exerciseOptions.find(opt => opt.Value === vest.ExerciseStrategy)
                : null;
              const errorIndex = errors?.vestingList && errors?.vestingList[index];

              return (
                <React.Fragment key={vest.key}>
                  <tr>
                    <td colSpan={3}>
                      <Box display="flex" alignItems="center" css="gap: 12px">
                        <Box display="flex" flexWrap="wrap" css="gap: 12px; width: 100%">
                          <Box css="flex-basis: 48%;">
                            <Label
                              css="display: block;"
                              mb="4"
                              mt="2"
                              variant={errorIndex?.VestingDate ? 'error' : undefined}
                            >
                              Vest Date
                            </Label>
                            <Controller
                              control={control}
                              name={`vestingList.${index}.VestingDate`}
                              render={({ field: { value, onChange, ref } }) => (
                                <SingleDatePicker
                                  inputRef={ref}
                                  date={value ? new Date(value) : undefined}
                                  onChange={onChange}
                                  maxDate={new Date(options.LatestVestingDate)}
                                  minDate={new Date(options.EarliestVestingDate)}
                                  isWrapperWidth
                                  formVariant={errorIndex?.VestingDate ? 'error' : undefined}
                                />
                              )}
                            />
                          </Box>
                          <Controller
                            control={control}
                            name={`vestingList.${index}.VestedShares`}
                            render={({ field: { value, onChange, ...field } }) => (
                              <InputNumberMask
                                label={
                                  grantType === GranTypeDTO.Rsu
                                    ? 'Shares to Vest'
                                    : 'Shares to Exercise'
                                }
                                {...field}
                                value={value}
                                allowNegative={false}
                                thousandSeparator={false}
                                aria-label="Vested shares"
                                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                                css="flex-basis: 48%;"
                                formVariant={errorIndex?.VestedShares ? 'error' : undefined}
                              />
                            )}
                          />
                          {(grantType === GranTypeDTO.Iso || grantType === GranTypeDTO.Nso) && (
                            <Controller
                              control={control}
                              name={`vestingList.${index}.PricePerShare`}
                              render={({ field: { onChange, ...field } }) => (
                                <InputNumberMask
                                  label="Strike Price"
                                  rightIcon="far fa-dollar-sign"
                                  decimalScale={2}
                                  allowNegative={false}
                                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                                  {...field}
                                  css="flex-basis: 48%;"
                                />
                              )}
                            />
                          )}
                          {exerciseOptions && (
                            <>
                              <Controller
                                name={`vestingList.${index}.ExerciseStrategy`}
                                control={control}
                                render={({ field: { value, onChange, ref } }) => (
                                  <Select
                                    isClearable
                                    innerRef={ref}
                                    label="Exercise Strategy"
                                    value={
                                      value !== null
                                        ? {
                                            value,
                                            label: exerciseOptions.find(opt => opt.Value === value)
                                              ?.Label,
                                          }
                                        : null
                                    }
                                    options={exerciseOptions.map(opt => ({
                                      value: opt.Value,
                                      label: opt.Label,
                                    }))}
                                    onChange={option => {
                                      onChange(option?.value ?? null);
                                      fieldArrayBag.update(index, {
                                        ...vestingList[index],
                                        ExerciseStrategy: option?.value ?? null,
                                        ExerciseDate: null,
                                        ExerciseYear: null,
                                      });
                                    }}
                                    isPortal
                                    formVariant={errorIndex?.ExerciseStrategy ? 'error' : undefined}
                                    css="flex-basis: 48%;"
                                  />
                                )}
                              />
                              {selectedExercise && selectedExercise.ShowDateSelector ? (
                                <Box css="flex-basis: 48%;">
                                  <Label
                                    css="display: block;"
                                    mb="4"
                                    variant={errorIndex?.ExerciseDate ? 'error' : undefined}
                                  >
                                    Exercise Date
                                  </Label>
                                  <Controller
                                    control={control}
                                    name={`vestingList.${index}.ExerciseDate`}
                                    render={({ field: { value, onChange, ref } }) => (
                                      <SingleDatePicker
                                        inputRef={ref}
                                        date={value ? new Date(value) : undefined}
                                        onChange={onChange}
                                        minDate={
                                          vest.VestingDate ? new Date(vest.VestingDate) : new Date()
                                        }
                                        isWrapperWidth
                                        formVariant={errorIndex?.ExerciseDate ? 'error' : undefined}
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                selectedExercise &&
                                selectedExercise.ShowYearSelector && (
                                  <Controller
                                    control={control}
                                    name={`vestingList.${index}.ExerciseYear`}
                                    render={({ field: { onChange, ...field } }) => (
                                      <InputNumberMask
                                        label="Years Until Exercise"
                                        decimalScale={0}
                                        maxLength={2}
                                        thousandSeparator={false}
                                        allowNegative={false}
                                        onValueChange={({ floatValue }) => {
                                          onChange(floatValue ? floatValue : '');
                                        }}
                                        formVariant={errorIndex?.ExerciseYear ? 'error' : undefined}
                                        {...field}
                                        css="flex-basis: 48%;"
                                      />
                                    )}
                                  />
                                )
                              )}
                            </>
                          )}
                          {dispositionOptions && (
                            <>
                              <Controller
                                name={`vestingList.${index}.DispositionStrategy`}
                                control={control}
                                render={({ field: { value, onChange, ref } }) => (
                                  <Select
                                    isClearable
                                    innerRef={ref}
                                    label="Disposition Strategy"
                                    value={
                                      value !== null
                                        ? {
                                            value,
                                            label: dispositionOptions.find(
                                              opt => opt.Value === value,
                                            )?.Label,
                                          }
                                        : null
                                    }
                                    options={dispositionOptions.map(opt => ({
                                      value: opt.Value,
                                      label: opt.Label,
                                    }))}
                                    onChange={option => {
                                      onChange(option?.value ?? null);
                                      fieldArrayBag.update(index, {
                                        ...vestingList[index],
                                        DispositionStrategy: option?.value ?? null,
                                        DispositionDate: null,
                                        DispositionYear: null,
                                      });
                                    }}
                                    isPortal
                                    formVariant={
                                      errorIndex?.DispositionStrategy ? 'error' : undefined
                                    }
                                    css="flex-basis: 48%;"
                                  />
                                )}
                              />
                              {selectedDisposition && selectedDisposition.ShowDateSelector ? (
                                <Box css="flex-basis: 48%;">
                                  <Label
                                    css="display: block;"
                                    mb="4"
                                    variant={errorIndex?.DispositionDate ? 'error' : undefined}
                                  >
                                    Disposition Date
                                  </Label>
                                  <Controller
                                    control={control}
                                    name={`vestingList.${index}.DispositionDate`}
                                    render={({ field: { value, onChange, ref } }) => (
                                      <SingleDatePicker
                                        inputRef={ref}
                                        date={value ? new Date(value) : undefined}
                                        onChange={onChange}
                                        minDate={
                                          vest.VestingDate ? new Date(vest.VestingDate) : new Date()
                                        }
                                        isWrapperWidth
                                        formVariant={
                                          errorIndex?.DispositionDate ? 'error' : undefined
                                        }
                                      />
                                    )}
                                  />
                                </Box>
                              ) : (
                                selectedDisposition &&
                                selectedDisposition.ShowYearSelector && (
                                  <Controller
                                    control={control}
                                    name={`vestingList.${index}.DispositionYear`}
                                    render={({ field: { onChange, ...field } }) => (
                                      <InputNumberMask
                                        label="Years Until Disposition"
                                        decimalScale={0}
                                        maxLength={2}
                                        thousandSeparator={false}
                                        allowNegative={false}
                                        onValueChange={({ floatValue }) =>
                                          onChange(floatValue ? floatValue : '')
                                        }
                                        formVariant={
                                          errorIndex?.DispositionYear ? 'error' : undefined
                                        }
                                        {...field}
                                        css="flex-basis: 48%;"
                                      />
                                    )}
                                  />
                                )
                              )}
                            </>
                          )}
                          {(grantType === GranTypeDTO.Iso || grantType === GranTypeDTO.Nso) && (
                            <Box css="flex-basis: 48%;">
                              <Label css="display: block;" mb="4" mt="2">
                                Expiration Date
                              </Label>
                              <Controller
                                control={control}
                                name={`vestingList.${index}.ExpirationDate`}
                                render={({ field: { value, onChange, ref } }) => (
                                  <SingleDatePicker
                                    inputRef={ref}
                                    date={value ? new Date(value) : undefined}
                                    onChange={onChange}
                                    isWrapperWidth
                                  />
                                )}
                              />
                            </Box>
                          )}
                        </Box>
                        <IconButton
                          icon="far fa-minus-circle"
                          onClick={() => {
                            if (vestingList.length === 1) {
                              fieldArrayBag.update(0, { ...emptyVestingSchedule });
                            } else {
                              fieldArrayBag.remove(index);
                            }
                          }}
                          variant="subtleInactive"
                          aria-label="Remove"
                        />
                      </Box>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </HtmlTable>

      <Button
        leftIcon="fas fa-plus"
        variant="link"
        onClick={() => {
          const lastItem = vestingList[vestingList.length - 1];

          if (lastItem) {
            const newVestingDate = lastItem.VestingDate
              ? moment(lastItem.VestingDate)
                  .add('1', 'years')
                  .format('MM/DD/YYYY')
              : '';
            fieldArrayBag.append({
              VestedShares:
                typeof lastItem.VestedShares === 'number'
                  ? Math.min(lastItem.VestedShares, sharesToAllocate < 0 ? 0 : sharesToAllocate)
                  : null,
              VestingDate: newVestingDate
                ? moment(newVestingDate).isBetween(
                    new Date(options.EarliestVestingDate),
                    new Date(options.LatestVestingDate),
                  )
                  ? newVestingDate
                  : null
                : null,
              DispositionStrategy: null,
              DispositionDate: null,
              DispositionYear: null,
              ExerciseStrategy: null,
              ExerciseDate: null,
              ExerciseYear: null,
              ExpirationDate: null,
              PricePerShare: null,
            });
          } else {
            fieldArrayBag.append({ ...emptyVestingSchedule });
          }
        }}
        my="16"
        ml="24"
      >
        Add Another Schedule
      </Button>
    </Box>
  );
};
